import {
  Box,
  Flex,
  Loader,
  Heading,
  Text,
  Label,
  Button,
  Icon,
  IconButton,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableColumn,
} from '@vibe/core';
import { Alert, Inbox, ExternalPage, LearnMore, Delete } from '@vibe/icons';
import { useState } from 'react';
import { mutate } from 'swr';
import { usePortals } from '../hooks/use-portals';
import useMonday from '../hooks/use-monday';
import { monday } from '../services/monday';
import { DeletePortalModal } from './DeletePortalModal';
import ErrorBox from './ErrorBox';

function AdminView() {
  const { allPortalsWithInfo, arePortalsLoading, portalsError } = usePortals();
  const { mondayContext } = useMonday();
  const isAdmin = mondayContext?.user.isAdmin ?? false;
  const [portalToDelete, setPortalToDelete] = useState<(typeof allPortalsWithInfo)[number] | null>(null);
  const [isRefreshingAfterDelete, setIsRefreshingAfterDelete] = useState(false);

  if (arePortalsLoading || isRefreshingAfterDelete) {
    return (
      <Flex
        justify="center"
        align="center"
        style={{
          width: '100%',
          height: '100vh',
          textAlign: 'center',
        }}
      >
        <Loader color="primary" size={30} />
      </Flex>
    );
  }

  if (portalsError) {
    return <ErrorBox headline="Error loading portals" description="Failed to load portal data. Please try again." />;
  }

  const COLUMNS: TableColumn[] = [
    {
      id: 'status',
      title: '',
      width: 40,
    },
    {
      id: 'clientsBoard',
      title: 'Clients Board',
      loadingStateType: 'medium-text',
      width: {
        min: 220,
        max: 350,
      },
    },
    {
      id: 'portalName',
      title: 'Portal Name',
      loadingStateType: 'medium-text',
      width: {
        min: 220,
        max: 350,
      },
    },
    {
      id: 'portalStatus',
      title: 'Portal Status',
      loadingStateType: 'medium-text',
      width: 150,
    },
    {
      id: 'customDomain',
      title: 'Custom Domain',
      loadingStateType: 'medium-text',
      width: 160,
    },
    {
      id: 'portalDomain',
      title: 'Portal Domain',
      loadingStateType: 'long-text',
      width: 350,
    },
    {
      id: 'actions',
      title: '',
    },
  ];

  return (
    <>
      <div
        style={{
          zIndex: 500,
          position: 'sticky',
          top: 0,
          padding: '0px 24px',
          width: 'calc(100% - 48px)',
          height: '72px',
          borderBottom: '1px solid #D0D4E4',
          backgroundColor: '#fff',
          boxShadow: '0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Flex
          direction="row"
          justify="start"
          align="center"
          gap={16}
          style={{
            width: '100%',
            height: 'inherit',
          }}
        >
          <Flex gap={12}>
            <div
              style={{
                overflow: 'hidden',
                borderRadius: '4px',
                lineHeight: 0,
              }}
            >
              <img src="/clientportalbuilder_app_icon.png" alt="Client Portal Builder" width={24} height={24} />
            </div>
            <Text type="text2" weight="medium" color="primary" align="start">
              Client Portal Builder
            </Text>
            <Text type="text2" color="secondary" align="start">
              Manage all portals created in this account
            </Text>
          </Flex>
          <div
            style={{
              marginLeft: 'auto',
            }}
          >
            <Flex gap={12}>
              <Button
                leftIcon={LearnMore}
                kind="tertiary"
                color="primary"
                size="xs"
                onClick={() => {
                  monday.execute('openLinkInTab', {
                    url: 'https://getgorilla.app/products/client-portal/docs/getting-started/app-views/client-portal-admin-view',
                  });
                }}
              >
                About this view
              </Button>
              <Button
                leftIcon={ExternalPage}
                kind="tertiary"
                color="primary"
                size="xs"
                onClick={() => {
                  monday.execute('openLinkInTab', { url: 'https://getgorilla.app/products/client-portal/docs/introduction' });
                }}
              >
                Documentation
              </Button>
            </Flex>
          </div>
        </Flex>
      </div>

      <div
        style={{
          overflow: 'hidden',
          overflowY: 'auto',
          padding: '32px',
          width: 'calc(100% - 64px)',
          height: 'calc(100vh - 137px)',
          backgroundColor: '#f6f7fb',
        }}
      >
        <div
          style={{
            overflow: 'hidden',
            overflowX: 'auto',
            width: '100%',
            maxWidth: '100%',
            height: 'auto',
            border: '1px solid #d0d4e4',
            borderRadius: '4px',
          }}
        >
          {!allPortalsWithInfo || allPortalsWithInfo.length === 0 ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '64px',
                height: 'calc(100vh - 267px)',
                backgroundColor: '#ffffff',
              }}
            >
              <Flex direction="column" gap={24}>
                <Flex direction="column" gap={8}>
                  <Flex direction="column" gap={4}>
                    <Icon icon={Inbox} size={26} />
                    <Heading type="h3" weight="medium" color="primary">
                      No portals found
                    </Heading>
                  </Flex>
                  <Text type="text2" color="secondary">
                    Learn how to set up your first portal
                  </Text>
                </Flex>
                <Flex direction="row" gap={16}>
                  <Button
                    kind="primary"
                    size="medium"
                    color="primary"
                    onClick={() => {
                      monday.execute('openLinkInTab', {
                        url: 'https://getgorilla.app/products/client-portal/docs/getting-started#anchor-6980',
                      });
                    }}
                  >
                    Getting started
                  </Button>
                </Flex>
              </Flex>
            </div>
          ) : (
            <Table
              columns={COLUMNS}
              size="large"
              emptyState={<></>}
              errorState={<></>}
              withoutBorder
              id="portal-table"
              className="admin--table"
            >
              <TableHeader className="admin--table-header">
                {COLUMNS.map((headerCell, index) => (
                  <TableHeaderCell
                    key={index}
                    title={headerCell.title ? headerCell.title : ''}
                    icon={headerCell.icon ? headerCell.icon : undefined}
                    infoContent={headerCell.infoContent ? headerCell.infoContent : undefined}
                  />
                ))}
              </TableHeader>
              <TableBody className="admin--table-body">
                {allPortalsWithInfo.map((portal, index) => {
                  return (
                    <TableRow key={index} className="admin--table-row">
                      <TableCell>
                        <div
                          style={{
                            pointerEvents: 'none',
                            opacity: portal.boardName ? 0 : 1,
                          }}
                        >
                          <IconButton
                            icon={Alert}
                            kind="primary"
                            color="negative"
                            size="xs"
                            onClick={() => {
                              // Void
                            }}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Text type="text2" color={portal.boardName ? 'primary' : 'negative'} ellipsis={false} style={{ maxWidth: '100%' }}>
                          {portal.boardName ? (
                            <a
                              href="#"
                              onClick={() => {
                                monday.execute('openLinkInTab', { url: `/boards/${portal.boardId}` });
                              }}
                              style={{
                                overflow: 'hidden',
                                display: 'inline-block',
                                maxWidth: '100%',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {portal.boardName}
                            </a>
                          ) : (
                            <>Board not found</>
                          )}
                        </Text>
                      </TableCell>
                      <TableCell>
                        <Text type="text2" color={portal.portalName ? 'primary' : 'secondary'} ellipsis={true} style={{ maxWidth: '100%' }}>
                          {portal.portalName ? portal.portalName : 'Client Portal'}
                        </Text>
                      </TableCell>
                      <TableCell>
                        {portal.isPublished ? (
                          <Label text="Published" color="positive" kind="fill" />
                        ) : (
                          <Label text="Unpublished" color="dark" kind="line" />
                        )}
                      </TableCell>
                      <TableCell>
                        {portal.customDomain ? (
                          <Label text="Connected" color="positive" kind="fill" />
                        ) : (
                          <Label text="Not connected" color="dark" kind="line" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Text type="text2" color="primary" ellipsis={true} style={{ maxWidth: '100%' }}>
                          {portal.isPublished ? (
                            <a
                              href="#"
                              onClick={() => {
                                monday.execute('openLinkInTab', { url: portal.portalUrl });
                              }}
                              style={{
                                overflow: 'hidden',
                                display: 'inline-block',
                                maxWidth: '100%',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {portal.customDomain ? <>{portal.customDomain}</> : <>{`${portal.portalId}_cp.clientportalbuilder.com`}</>}
                            </a>
                          ) : portal.customDomain ? (
                            <>{portal.customDomain}</>
                          ) : (
                            <>{`${portal.portalId}_cp.clientportalbuilder.com`}</>
                          )}
                        </Text>
                      </TableCell>
                      <TableCell>
                        {isAdmin ? (
                          <Button
                            leftIcon={Delete}
                            kind="primary"
                            color="negative"
                            size="xs"
                            onClick={() => {
                              setPortalToDelete(portal);
                            }}
                          >
                            Delete
                          </Button>
                        ) : (
                          <>&nbsp;</>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      {portalToDelete ? (
        <DeletePortalModal
          isOpen
          portalName={portalToDelete.portalName}
          boardName={portalToDelete.boardName}
          portalId={portalToDelete.portalId}
          boardId={portalToDelete.boardId}
          onClose={async (didDelete) => {
            setPortalToDelete(null);
            if (didDelete) {
              setIsRefreshingAfterDelete(true);
              try {
                await mutate('/portals');
              } finally {
                setIsRefreshingAfterDelete(false);
              }
            }
          }}
        />
      ) : null}
    </>
  );
}

export default AdminView;
